import React from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  IconButton,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { FlowComponent } from '../../../../flow-core/FlowComponent';
import { NavigationListCore } from '../../../../flow-core/components/NavigationListCore';

// Auto-generate a stable, unique item id from its human-readable title so users
// never have to author internal ids by hand. Slug the title (fallback "item")
// and append a counter until unique within the list. Generated once at add-time.
const slugify = (text) =>
  String(text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const generateItemId = (title, existingIds) => {
  const base = slugify(title) || 'item';
  let candidate = base;
  let n = 2;
  while (existingIds.includes(candidate)) {
    candidate = `${base}_${n}`;
    n += 1;
  }
  return candidate;
};

export const NavigationListComponent = new FlowComponent({
  ...NavigationListCore,
  renderPreview(properties) {
    return (
      <Box sx={{ opacity: properties.visible === false ? 0.5 : 1, mt: 2 }}>
        {properties.label && (
          <Typography variant="subtitle2" gutterBottom>
            {properties.label}
          </Typography>
        )}
        {properties.description && (
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {properties.description}
          </Typography>
        )}
        {/* As WhatsApp lists them: plain rows, no box or chevron; title, description and
            metadata on the left, the end title and description on the right. */}
        {(properties['list-items'] || []).map((item, index) => (
          <Box key={item.id || index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 1.25 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: 16, color: 'text.primary' }}>
                  {item['main-content']?.title || 'Untitled'}
                </Typography>
                {item.badge && <Chip label={item.badge} size="small" color="primary" />}
              </Box>
              {item['main-content']?.description && (
                <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{item['main-content'].description}</Typography>
              )}
              {item['main-content']?.metadata && (
                <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{item['main-content'].metadata}</Typography>
              )}
              {item.tags && item.tags.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                  {item.tags.map((tag, tagIdx) => (
                    <Chip key={tagIdx} label={tag} size="small" variant="outlined" />
                  ))}
                </Box>
              )}
            </Box>
            {item.end && (
              <Box sx={{ textAlign: 'right' }}>
                {item.end.title && <Typography sx={{ fontSize: 16, color: 'text.primary' }}>{item.end.title}</Typography>}
                {item.end.description && (
                  <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{item.end.description}</Typography>
                )}
              </Box>
            )}
          </Box>
        ))}
      </Box>
    );
  },
  renderPropertyEditor(properties, onChange) {
    var handleItemChange = (index, path, value) => {
      var currentItems = properties['list-items'] || [];
      var newItems = [...currentItems];
      var item = { ...newItems[index] };

      if (path.includes('.')) {
        var parts = path.split('.');
        if (!item[parts[0]]) {
          item[parts[0]] = {};
        }
        item[parts[0]] = { ...item[parts[0]], [parts[1]]: value };
      } else {
        item[path] = value;
      }

      newItems[index] = item;
      onChange('list-items', newItems);
    };

    var handleAddItem = () => {
      var currentItems = properties['list-items'] || [];
      if (currentItems.length < 20) {
        var title = 'New Item';
        var newItem = {
          id: generateItemId(title, currentItems.map((i) => i.id)),
          'main-content': {
            title,
            description: '',
          },
          'on-click-action': {
            name: 'navigate',
            next: { type: 'screen', name: '' },
          },
        };
        onChange('list-items', [...currentItems, newItem]);
      }
    };

    var handleRemoveItem = (index) => {
      var currentItems = properties['list-items'] || [];
      if (currentItems.length > 1) {
        onChange('list-items', currentItems.filter((_, i) => i !== index));
      }
    };

    var handleTagsChange = (index, value) => {
      var tags = value.split(',').map((t) => t.trim()).filter((t) => t).slice(0, 3);
      handleItemChange(index, 'tags', tags);
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="info">
          NavigationList creates a clickable list. Max 20 items, max 2 per screen, only 1 badge per list.
        </Alert>

        <TextField
          fullWidth
          label="Field Name"
          value={properties.name || ''}
          onChange={(e) => onChange('name', e.target.value)}
          required
          size="small"
          helperText="Used to identify this field in the form data"
        />

        <TextField
          fullWidth
          label="Label"
          value={properties.label || ''}
          onChange={(e) => onChange('label', e.target.value)}
          size="small"
        />

        <TextField
          fullWidth
          label="Description"
          value={properties.description || ''}
          onChange={(e) => onChange('description', e.target.value)}
          size="small"
          multiline
          rows={2}
        />

        <FormControl fullWidth size="small">
          <InputLabel>Media Size</InputLabel>
          <Select
            value={properties['media-size'] || 'regular'}
            onChange={(e) => onChange('media-size', e.target.value)}
            label="Media Size"
          >
            <MenuItem value="regular">Regular</MenuItem>
            <MenuItem value="large">Large</MenuItem>
          </Select>
        </FormControl>

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          List Items ({(properties['list-items'] || []).length}/20)
        </Typography>

        {(properties['list-items'] || []).map((item, index) => (
          <Accordion key={item.id || index} defaultExpanded={index === 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography variant="body2">
                  {item['main-content']?.title || `Item ${index + 1}`}
                </Typography>
                {item.badge && (
                  <Chip label="Has Badge" size="small" color="primary" />
                )}
                <Box sx={{ flex: 1 }} />
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveItem(index);
                  }}
                  color="error"
                  disabled={(properties['list-items'] || []).length <= 1}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Main Content
                </Typography>
                <TextField
                  size="small"
                  label="Title"
                  value={item['main-content']?.title || ''}
                  onChange={(e) => handleItemChange(index, 'main-content.title', e.target.value)}
                  required
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Description"
                  value={item['main-content']?.description || ''}
                  onChange={(e) => handleItemChange(index, 'main-content.description', e.target.value)}
                  multiline
                  rows={2}
                />
                <TextField
                  size="small"
                  label="Metadata"
                  value={item['main-content']?.metadata || ''}
                  onChange={(e) => handleItemChange(index, 'main-content.metadata', e.target.value)}
                />

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  End Content (Optional)
                </Typography>
                <TextField
                  size="small"
                  label="End Title"
                  value={item.end?.title || ''}
                  onChange={(e) => handleItemChange(index, 'end.title', e.target.value)}
                />
                <TextField
                  size="small"
                  label="End Description"
                  value={item.end?.description || ''}
                  onChange={(e) => handleItemChange(index, 'end.description', e.target.value)}
                />

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Additional
                </Typography>
                <TextField
                  size="small"
                  label="Badge (max 1 per list)"
                  value={item.badge || ''}
                  onChange={(e) => handleItemChange(index, 'badge', e.target.value)}
                />
                <TextField
                  size="small"
                  label="Tags (comma-separated, max 3)"
                  value={(item.tags || []).join(', ')}
                  onChange={(e) => handleTagsChange(index, e.target.value)}
                  helperText="E.g.: New, Featured, Popular"
                />

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Action (Required)
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Action Type</InputLabel>
                  <Select
                    value={item['on-click-action']?.name || 'navigate'}
                    onChange={(e) => {
                      var newAction = { name: e.target.value };
                      if (e.target.value === 'navigate') {
                        newAction.next = { type: 'screen', name: item['on-click-action']?.next?.name || '' };
                      } else {
                        newAction.payload = item['on-click-action']?.payload || {};
                      }
                      handleItemChange(index, 'on-click-action', newAction);
                    }}
                    label="Action Type"
                  >
                    <MenuItem value="navigate">Navigate to Screen</MenuItem>
                    <MenuItem value="complete">Complete Flow</MenuItem>
                    <MenuItem value="data_exchange">Data Exchange</MenuItem>
                  </Select>
                </FormControl>
                {item['on-click-action']?.name === 'navigate' && (
                  <TextField
                    size="small"
                    label="Next Screen ID"
                    value={item['on-click-action']?.next?.name || ''}
                    onChange={(e) => handleItemChange(index, 'on-click-action', {
                      name: 'navigate',
                      next: { type: 'screen', name: e.target.value },
                    })}
                    helperText="ID of the screen to navigate to"
                  />
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}

        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          variant="outlined"
          disabled={(properties['list-items'] || []).length >= 20}
        >
          Add Item
        </Button>

        <Divider sx={{ my: 1 }} />

        <FormControlLabel
          control={
            <Switch
              checked={properties.visible !== false}
              onChange={(e) => onChange('visible', e.target.checked)}
            />
          }
          label="Visible"
        />
      </Box>
    );
  },
});
